import {Component, effect, input, output, signal, Signal} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {AbstractControl, FormBuilder, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {CustomerModel} from "../../../models/resources/customer.model";
import {LanguageService} from "../../../services/resources/language.service";
import {FormInputComponent} from "../../utils/form-input/form-input.component";
import {FormSelectComponent} from "../../utils/form-select/form-select.component";
import {TranslateModule} from "@ngx-translate/core";
import {ApiSelectComponent} from "../../utils/api-select/api-select.component";
import {CountryService} from "../../../services/resources/country.service";
import {IndustryService} from "../../../services/resources/industry.service";
import {CategoryService} from "../../../services/resources/category.service";
import {StateService} from "../../../services/resources/state.service";
import {TextAreaInputComponent} from "../../utils/text-area-input/text-area-input.component";
import {LanguagePipe} from "../../../pipes/language.pipe";
import {UrlParamModel} from "../../../models/url-param.model";
import {CompanyType} from "../../../enums/company-type";
import {EnumSelectComponent} from "../../utils/enum-select/enum-select.component";
import {castCompanyTypeEnum} from "../../../enums/company-type";
import {castContactTypeEnum, ContactType} from "../../../enums/contact-type";
import {CheckboxInputComponent} from "../../utils/checkbox-input/checkbox-input.component";
import {CustomerService} from "../../../services/resources/customer.service";
import {computed} from "@angular/core";
import {LinkSelectComponent} from "../../utils/link-select/link-select.component";
import {ReadOnlyInputComponent} from "../../utils/read-only-input/read-only-input.component";

@Component({
  selector: 'app-customer-form',
  imports: [
    ReactiveFormsModule,
    FormInputComponent,
    FormSelectComponent,
    EnumSelectComponent,
    TranslateModule,
    ApiSelectComponent,
    TextAreaInputComponent,
    LinkSelectComponent,
    ReadOnlyInputComponent,
    CheckboxInputComponent
  ],
  templateUrl: './customer-form.component.html',
  styleUrl: './customer-form.component.scss',
  providers: [
    LanguagePipe
  ]
})
export class CustomerFormComponent {
  formGroupOutput = output<FormGroup>()
  formGroup: Signal<FormGroup>;
  customer = input<CustomerModel>();
  /** Wenn gesetzt: "Kontakt hinzufügen"-Modus – Unternehmen wird read-only angezeigt. */
  parentCustomerForDisplay = input<CustomerModel | null>(null);

  /** Stabile Referenz, damit api-select nicht bei jeder CD neu lädt. */
  readonly emptyQueryParams: UrlParamModel[] = [];

  readonly companyTypeEnum = CompanyType;
  readonly castCompanyType = castCompanyTypeEnum;
  readonly contactTypeEnum = ContactType;
  readonly castContactType = castContactTypeEnum;

  /** IANA-Zeitzonen für das Zeitzonen-Select (entspricht Odoo tz). */
  readonly timezones: { name: string }[] = Intl.supportedValuesOf('timeZone').map(tz => ({name: tz}));

  /** Kontakt-Typ (Kontakt/Rechnung/Lieferung/Andere) nur bei Child-Kontakten anzeigen – wie in Odoo. */
  isChildContact!: Signal<boolean>;

  /**
   * Validiert USt-IdNr.-Format (z. B. ATU12345675).
   * Erwartet: 2–3 Buchstaben (Ländercode ggf. + U) + 6–12 Ziffern, optional Leerzeichen dazwischen.
   */
  static vatFormatValidator(control: AbstractControl): { vatFormat: true } | null {
    const value = control.value;
    if (value == null || String(value).trim() === '') {
      return null;
    }
    const normalized = String(value).trim().replace(/\s+/g, '');
    if (!/^[A-Za-z]{2,3}\d{6,12}$/.test(normalized)) {
      return { vatFormat: true };
    }
    // Österreichische UID: Prüfziffer validieren (Odoo lehnt UIDs mit falscher Prüfziffer beim Sync ab)
    const atMatch = /^ATU(\d{8})$/i.exec(normalized);
    if (atMatch && !CustomerFormComponent.isValidAustrianVatCheckDigit(atMatch[1])) {
      return { vatFormat: true };
    }
    return null;
  }

  /**
   * Prüfziffern-Check für ATU-Nummern (8 Ziffern, letzte = Prüfziffer):
   * Ziffern 1–7 abwechselnd mit 1 und 2 gewichten, bei zweistelligen Produkten die Quersumme,
   * Prüfziffer = (96 − Summe) mod 10.
   */
  private static isValidAustrianVatCheckDigit(digits: string): boolean {
    let sum = 0;
    for (let i = 0; i < 7; i++) {
      const product = parseInt(digits[i], 10) * (i % 2 === 0 ? 1 : 2);
      sum += product > 9 ? product - 9 : product;
    }
    return (96 - sum) % 10 === parseInt(digits[7], 10);
  }

  constructor(
    private formBuilder: FormBuilder,
    private languagePipe: LanguagePipe,
    public customerService: CustomerService,
    public countryService: CountryService,
    public industryService: IndustryService,
    public categoryService: CategoryService,
    public stateService: StateService,
    public languageService: LanguageService
  ) {
    this.formGroup = signal(this.formBuilder.group({
      company: [null, [Validators.required]],
      companyType: [CompanyType.COMPANY, [Validators.required]],
      type: [ContactType.CONTACT],
      timezone: [null],
      active: [true],
      vat: [null, [CustomerFormComponent.vatFormatValidator]],
      website: [null, []],
      comment: [null, []],
      companyRegistry: [null, []],
      reference: [null, []],
      erpIndustryId: [null, []],
      erpCategoryIds: [[], []],
      languageId: [null, [Validators.required]],
      parent: [null, []],
      person: this.formBuilder.group({
        name: [null, []],
        function: [null, []],
        phoneNumber: [null, []],
        email: [null, [Validators.email]],
      }),
      address: this.formBuilder.group({
        postcode: [null, []],
        place: [null, []],
        street: [null, []],
        houseNumber: [null, []],
        addressSuffix: [null, []],
        erpCountryId: [null, []],
        erpStateId: [null, []],
      })
    }));

    // Child-Kontakt auch erkennen, wenn im "Neuer Kunde"-Modal ein Parent-Unternehmen gewählt wird
    const selectedParent = toSignal(this.formGroup().get('parent')!.valueChanges, {initialValue: null});
    this.isChildContact = computed(() =>
      this.parentCustomerForDisplay() != null || this.customer()?.parentId != null || selectedParent() != null
    );

    effect(() => {
      this.formGroupOutput.emit(this.formGroup());
    });
    effect(() => {
      if (this.customer())
        this.updateFormValues();
    });
    this.formGroup().get('companyType')?.valueChanges.subscribe((companyType: CompanyType) => {
      this.applyCompanyTypeValidators(companyType);
    });
  }

  /** Setzt company required bei Typ Company, person.name required bei Typ Person. */
  private applyCompanyTypeValidators(companyType: CompanyType): void {
    const companyCtrl = this.formGroup().get('company');
    if (companyCtrl) {
      if (companyType === CompanyType.PERSON) {
        companyCtrl.clearValidators();
      } else {
        companyCtrl.setValidators([Validators.required]);
      }
      companyCtrl.updateValueAndValidity({ emitEvent: false });
    }
    const personNameCtrl = this.getPersonFormGroup().get('name');
    if (personNameCtrl) {
      if (companyType === CompanyType.PERSON) {
        personNameCtrl.setValidators([Validators.required]);
      } else {
        personNameCtrl.clearValidators();
      }
      personNameCtrl.updateValueAndValidity({ emitEvent: false });
    }
  }

  updateFormValues() {
    const currentModel = this.customer();
    if (currentModel) {
      this.formGroup().patchValue({
        company: currentModel.company,
        companyType: currentModel.companyType ?? CompanyType.COMPANY,
        type: currentModel.type ?? ContactType.CONTACT,
        timezone: currentModel.timezone ?? null,
        active: currentModel.active ?? true,
        vat: currentModel.vat ?? null,
        website: currentModel.website ?? null,
        comment: currentModel.comment ?? null,
        companyRegistry: currentModel.companyRegistry ?? null,
        reference: currentModel.reference ?? null,
        erpIndustryId: currentModel.erpIndustryId ?? null,
        erpCategoryIds: currentModel.erpCategoryIds ?? [],
        languageId: currentModel.language?.id ?? null,
        parent: currentModel.parent ?? null,
      });
      this.getPersonFormGroup().patchValue({
        name: currentModel.person?.name ?? null,
        function: currentModel.person?.function ?? null,
        phoneNumber: currentModel.person?.phoneNumber ?? null,
        email: currentModel.person?.email ?? null,
      });
      if (currentModel.address) {
        this.getAddressFormGroup().patchValue({
          postcode: currentModel.address.postcode,
          place: currentModel.address.place,
          street: currentModel.address.street,
          houseNumber: currentModel.address.houseNumber,
          addressSuffix: currentModel.address.addressSuffix ?? null,
          erpCountryId: currentModel.address.erpCountryId ?? null,
          erpStateId: currentModel.address.erpStateId ?? null,
        });
      }
      this.applyCompanyTypeValidators(currentModel.companyType ?? CompanyType.COMPANY);
    }
  }

  translateValue = (value: any) => this.languagePipe.transform(value);

  getPersonFormGroup(): FormGroup {
    return this.formGroup().get('person') as FormGroup;
  }

  getAddressFormGroup(): FormGroup {
    return this.formGroup().get('address') as FormGroup;
  }

  private stateQueryParamsCache: { countryId: number | null; params: UrlParamModel[] } = { countryId: null, params: [] };

  getStateQueryParams(): UrlParamModel[] {
    const countryId = this.getAddressFormGroup().get('erpCountryId')?.value;
    if (countryId == null || countryId === '') {
      if (this.stateQueryParamsCache.countryId === null) return this.stateQueryParamsCache.params;
      this.stateQueryParamsCache = { countryId: null, params: [] };
      return this.stateQueryParamsCache.params;
    }
    const id = Number(countryId);
    if (this.stateQueryParamsCache.countryId === id) return this.stateQueryParamsCache.params;
    this.stateQueryParamsCache = { countryId: id, params: [{ key: 'country_id', value: String(countryId) }] };
    return this.stateQueryParamsCache.params;
  }
}
